<script>
// @ts-check

import { updateToken } from '@/api/oauth'
import CardDialog from '@/components/dialogs/CardDialog.vue'
import {
  LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME,
  mobileTimeouts
} from '@/core/constants'
import { differenceInDays } from 'date-fns'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'SessionExpirationDialog',
  components: {
    CardDialog
  },
  data: () => ({
    /** @type {number|undefined} */
    minutesLeft: undefined,
    /** @type {NodeJS.Timer|undefined} */
    interval: undefined,
    /** @type {boolean} */
    isRefreshingToken: false
  }),
  computed: {
    ...mapGetters('auth', ['sessionExpirationDialogOpen']),
    ...mapGetters('mobileData', ['isMobileWrapperUser'])
  },
  mounted() {
    // We want to check the expiration time straight away when user opens the mobile application
    // to prompt logging in again before they can do anything if token expires in the next 14 days
    if (this.isMobileWrapperUser) {
      this.checkMobileExpiration()
    } else {
      // Check the expiration time right away on desktop as well
      this.checkDesktopExpiration()
      // Check every 30 seconds on desktop
      this.setDesktopExpirationInterval()
    }
  },
  beforeUnmount() {
    clearInterval(this.interval)
  },
  methods: {
    ...mapMutations('auth', ['setSessionExpirationDialogOpen']),
    checkMobileExpiration() {
      const sessionExpiration = localStorage.getItem(
        LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
      )

      if (!sessionExpiration) {
        return
      }

      const differenceInDaystoExpiration = differenceInDays(
        new Date(sessionExpiration),
        new Date()
      )

      if (differenceInDaystoExpiration <= 0) {
        this.expirationLogout(this.$t('login.system_logout'))
        return
      }

      if (
        differenceInDaystoExpiration <=
        mobileTimeouts.SESSION_EXPIRING_THRESHOLD
      ) {
        this.setSessionExpirationDialogOpen(true)
      }
    },
    checkDesktopExpiration() {
      const sessionExpiration = localStorage.getItem(
        LOCAL_STORAGE_KEY_SESSION_EXPIRATION_TIME
      )

      if (!sessionExpiration) {
        return
      }

      const currentTime = Math.floor(new Date().getTime() / 1000)
      const expirationTime = Math.floor(
        new Date(sessionExpiration).getTime() / 1000
      )
      const secondsUntilExpiration = expirationTime - currentTime

      if (secondsUntilExpiration <= 300 && secondsUntilExpiration > 30) {
        this.minutesLeft = Math.ceil(secondsUntilExpiration / 60)
        this.setSessionExpirationDialogOpen(true)
      } else if (secondsUntilExpiration <= 30) {
        this.setSessionExpirationDialogOpen(false)
        this.expirationLogout(this.$t('login.system_logout'))
      } else {
        this.setSessionExpirationDialogOpen(false)
      }
    },
    setDesktopExpirationInterval() {
      this.interval = setInterval(() => {
        this.checkDesktopExpiration()
      }, 1000 * 30)
    },
    refreshToken() {
      this.isRefreshingToken = true

      updateToken()
        .then(() => {
          this.minutesLeft = undefined
        })
        .catch(() => {
          // Ignore as updateToken has already logged out the user
        })
        .finally(() => {
          this.setSessionExpirationDialogOpen(false)
          this.isRefreshingToken = false
        })
    },
    /**
     * @param {import('vue-i18n').TranslateResult|string} message
     */
    expirationLogout(message) {
      this.$store.dispatch('auth/logout', { message })
    },
    logout() {
      this.$store.dispatch('auth/logout')
    }
  }
}
</script>

<template>
  <CardDialog
    v-if="sessionExpirationDialogOpen"
    persistent
    :title="
      isMobileWrapperUser
        ? $t('login.session_expires_mobile')
        : $t('login.session_expires_in', { minutes: minutesLeft })
    "
    :disabled="isRefreshingToken"
    @action="refreshToken"
    @close="logout"
  >
    <p>
      {{
        isMobileWrapperUser
          ? $t('login.session_expiring_info_mobile')
          : $t('login.session_expiring_info')
      }}
    </p>

    <template #cancel>
      {{ $t('login.logout') }}
    </template>

    <template #action>
      {{ $t('login.continue_session') }}
    </template>

    <template v-if="isMobileWrapperUser" #actions="{ close }">
      <PrimaryButton class="w-full" @click.stop="close">
        {{ $t('login.logout') }}
      </PrimaryButton>
    </template>
  </CardDialog>
</template>
